import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiRefreshCw,
  FiCalendar,
  FiFilter,
  FiChevronDown,
  FiChevronUp,
  FiCheckSquare,
  FiClock,
  FiAlertCircle,
  FiList,
  FiSearch,
  FiBarChart2,
  FiPackage,
  FiTrendingUp,
  FiInfo,
} from "react-icons/fi";

import { useAuth } from "../../../services/useAuth";
import { useTitle } from "../../../hooks/useTitle";
import { withAuth } from "../../../components/HOC/withAuth";

import AppPage from "../../../components/CustomUI/AppPage";
import CardContainer from "../../../components/CustomUI/CardContainer";
import CardInfo from "../../../components/CustomUI/CardInfo";
import Container from "../../../components/CustomUI/Container";
import ContainerItem from "../../../components/CustomUI/Container/ContainerItem";
import { Table, Tr, Td } from "../../../components/BaseUI/Table";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import Button from "../../../components/BaseUI/Button";
import Input from "../../../components/BaseUI/Input";
import Select from "../../../components/BaseUI/Select";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../components/BaseUI/Modal";
import {
  Tabs,
  TabList,
  Tab,
  TabPanel,
  Panel,
} from "../../../components/BaseUI/Tabs";

import styles from "./ProductionPlanning.module.scss";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const STATUS_OPTIONS = [
  { value: "planned", label: "Planlandı" },
  { value: "in_progress", label: "Devam Ediyor" },
  { value: "completed", label: "Tamamlandı" },
  { value: "cancelled", label: "İptal Edildi" },
];

const STATUS_COLORS = {
  planned: "#3b82f6",
  in_progress: "#f59e0b",
  completed: "#10b981",
  cancelled: "#ef4444",
};

const STATUS_LABELS = {
  planned: "Planlandı",
  in_progress: "Devam Ediyor",
  completed: "Tamamlandı",
  cancelled: "İptal Edildi",
};

const EMPTY_FORM = {
  title: "",
  partId: "",
  productionLineId: "",
  plannedQuantity: "",
  startDate: "",
  endDate: "",
  operatorNote: "",
  status: "planned",
};

const PLANS_COLUMNS = [
  { name: "Plan Adı", dataType: "text", id: 0 },
  { name: "Parça", dataType: "text", id: 1 },
  { name: "Üretim Hattı", dataType: "text", id: 2 },
  { name: "Planlanan", dataType: "number", id: 3 },
  { name: "Gerçekleşen", dataType: "number", id: 4 },
  { name: "İlerleme", dataType: "text", id: 5, sortable: false },
  { name: "Tarih Aralığı", dataType: "text", id: 6 },
  { name: "Durum", dataType: "text", id: 7, sortable: false },
  { name: "İşlemler", dataType: "text", id: 8, sortable: false },
];

const FEASIBILITY_COLUMNS = [
  { name: "Parça", dataType: "text", id: 0 },
  { name: "Kaynak", dataType: "text", id: 1 },
  { name: "Maks. Üretilebilir", dataType: "number", id: 3 },
  { name: "Eksik Bileşen", dataType: "number", id: 4 },
];

const BOM_SOURCE_LABELS = {
  finalProduct: "Son Ürün",
  partMachine: "Üretim",
  partAssembly: "Montaj",
};

const SHORTAGE_COLUMNS = [
  { name: "Bileşen", dataType: "text", id: 0 },
  { name: "Kod", dataType: "text", id: 1 },
  { name: "Tüketim / Adet", dataType: "number", id: 2 },
  { name: "Toplam İhtiyaç", dataType: "number", id: 3 },
  { name: "Mevcut Stok", dataType: "number", id: 4 },
  { name: "Eksik Miktar", dataType: "number", id: 5 },
  { name: "Karşılanabilir", dataType: "text", id: 6, sortable: false },
  { name: "Durum", dataType: "text", id: 7, sortable: false },
];

// ─── Status Badge ─────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => (
  <span
    className={styles.statusBadge}
    style={{
      background: STATUS_COLORS[status] + "22",
      color: STATUS_COLORS[status],
    }}
  >
    {STATUS_LABELS[status] || status}
  </span>
);

// ─── Progress Bar ─────────────────────────────────────────────────────────────
const ProgressBar = ({ value, max, color = "#10b981" }) => {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className={styles.progressWrap}>
      <div className={styles.progressTrack}>
        <div
          className={styles.progressFill}
          style={{ width: `${pct}%`, background: color }}
        />
      </div>
      <span className={styles.progressPct}>{pct}%</span>
    </div>
  );
};

// ─── BOM Detail Table (reusable inline) ──────────────────────────────────────
const BomDetailTable = ({ bomLines, styles: s, indent = 0 }) => (
  <div
    className={s.bomDetail}
    style={indent ? { marginLeft: indent } : undefined}
  >
    <div className={s.bomDetailTitle}>
      <FiList size={14} /> Malzeme Listesi (BOM) — 1 adet üretim için
    </div>
    <table className={s.bomTable}>
      <thead>
        <tr>
          <th>Bileşen</th>
          <th>Tüketim / Adet</th>
          <th>Mevcut Stok</th>
          <th>Karşılanan Üretim</th>
          <th>Durum</th>
        </tr>
      </thead>
      <tbody>
        {bomLines.map((bline, i) => (
          <tr key={i}>
            <td>
              <div style={{ fontWeight: 600 }}>{bline.partName}</div>
              <div
                style={{ fontSize: 11, color: "var(--m-text-subtle, #9ca3af)" }}
              >
                {bline.partCode}
              </div>
            </td>
            <td>{bline.consumptionRatio} / adet</td>
            <td>
              <strong
                style={{
                  color: bline.availableQty > 0 ? undefined : "#ef4444",
                }}
              >
                {bline.availableQty}
              </strong>
            </td>
            <td>
              <strong
                style={{
                  color: bline.coverageUnits > 0 ? "#10b981" : "#ef4444",
                }}
              >
                {bline.coverageUnits} adet
              </strong>
            </td>
            <td>
              {bline.shortageQty > 0 ? (
                <span
                  style={{ color: "#ef4444", fontSize: 12, fontWeight: 600 }}
                >
                  {bline.shortageQty} eksik
                </span>
              ) : (
                <span style={{ color: "#10b981", fontSize: 12 }}>
                  ✓ Yeterli
                </span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const Feasibility = () => {
  const { getToken } = useAuth();
  useTitle("Üretilebilirlik Analizi");

  const [toasts, setToasts] = useState([]);

  // Plans
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterPartId, setFilterPartId] = useState("");
  const [filterStartDate, setFilterStartDate] = useState("");
  const [filterEndDate, setFilterEndDate] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formLoading, setFormLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Feasibility
  const [feasibility, setFeasibility] = useState([]);
  const [feasibilityLoading, setFeasibilityLoading] = useState(false);
  const [expandedFeasRow, setExpandedFeasRow] = useState(null);
  const [expandedVariantRow, setExpandedVariantRow] = useState(null);

  // Shortage
  const [shortageMode, setShortageMode] = useState("part"); // "part" | "product"
  const [shortageMainPartId, setShortageMainPartId] = useState("");
  const [shortageVariantId, setShortageVariantId] = useState("");
  const [shortageModelId, setShortageModelId] = useState("");
  const [shortageVariants, setShortageVariants] = useState([]);
  const [shortageVariantsLoading, setShortageVariantsLoading] = useState(false);
  const [shortageQty, setShortageQty] = useState("");
  const [shortageResult, setShortageResult] = useState(null);
  const [shortageLoading, setShortageLoading] = useState(false);

  // Shared
  const [parts, setParts] = useState([]);
  const [lines, setLines] = useState([]);
  const [deviceModels, setDeviceModels] = useState([]);

  const authHeaders = useCallback(
    () => ({ Authorization: `Bearer ${getToken()}` }),
    [getToken],
  );

  const addToast = useCallback((message, type = "success") => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(
      () => setToasts((prev) => prev.filter((t) => t.id !== id)),
      4000,
    );
  }, []);

  const fetchParts = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/parts`, {
        headers: authHeaders(),
      });
      const list = res.data?.parts ?? res.data?.data ?? [];
      setParts(Array.isArray(list) ? list : []);
    } catch {
      setParts([]);
    }
  }, [authHeaders]);

  const fetchLines = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/production-lines`, {
        headers: authHeaders(),
      });
      const list = res.data?.lines ?? res.data?.data ?? [];
      setLines(Array.isArray(list) ? list : []);
    } catch {
      setLines([]);
    }
  }, [authHeaders]);

  const fetchDeviceModels = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/device-models`, {
        headers: authHeaders(),
      });
      const list = res.data?.models ?? res.data?.deviceModels ?? res.data ?? [];
      setDeviceModels(Array.isArray(list) ? list : []);
    } catch {
      setDeviceModels([]);
    }
  }, [authHeaders]);

  const fetchPlans = useCallback(async () => {
    setPlansLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterPartId) params.partId = filterPartId;
      if (filterStartDate) params.startDate = filterStartDate;
      if (filterEndDate) params.endDate = filterEndDate;
      const res = await axios.get(`${API_URL}/api/production-planning/plans`, {
        headers: authHeaders(),
        params,
      });
      setPlans(res.data?.data || []);
    } catch {
      setPlans([]);
    } finally {
      setPlansLoading(false);
    }
  }, [authHeaders, filterStatus, filterPartId, filterStartDate, filterEndDate]);

  const fetchFeasibility = useCallback(async () => {
    setFeasibilityLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/production-planning/feasibility`,
        {
          headers: authHeaders(),
        },
      );
      setFeasibility(res.data?.data || []);
    } catch {
      setFeasibility([]);
    } finally {
      setFeasibilityLoading(false);
    }
  }, [authHeaders]);

  const fetchPartVariants = useCallback(
    async (mainId) => {
      if (!mainId) {
        setShortageVariants([]);
        setShortageVariantId("");
        return;
      }
      setShortageVariantsLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/parts/${mainId}`, {
          headers: authHeaders(),
        });
        const vars = res.data?.variants || [];
        setShortageVariants(vars);
        setShortageVariantId("");
      } catch {
        setShortageVariants([]);
        setShortageVariantId("");
      } finally {
        setShortageVariantsLoading(false);
      }
    },
    [authHeaders],
  );

  const fetchPlansRef = useRef(fetchPlans);
  const fetchFeasibilityRef = useRef(fetchFeasibility);
  fetchPlansRef.current = fetchPlans;
  fetchFeasibilityRef.current = fetchFeasibility;

  const handleTabChange = useCallback((tabIndex) => {
    if (tabIndex === 0) fetchPlansRef.current();
    else if (tabIndex === 1) fetchFeasibilityRef.current();
  }, []);

  useEffect(() => {
    fetchParts();
    fetchLines();
    fetchDeviceModels();
    fetchFeasibility();
  }, [fetchParts, fetchLines, fetchDeviceModels, fetchFeasibility]);

  const openCreate = () => {
    setEditingPlan(null);
    setForm(EMPTY_FORM);
    setModalOpen(true);
  };
  const openEdit = (plan) => {
    setEditingPlan(plan);
    setForm({
      title: plan.title || "",
      partId: plan.partId?._id || plan.partId || "",
      productionLineId:
        plan.productionLineId?._id || plan.productionLineId || "",
      plannedQuantity: plan.plannedQuantity ?? "",
      startDate: plan.startDate ? plan.startDate.slice(0, 10) : "",
      endDate: plan.endDate ? plan.endDate.slice(0, 10) : "",
      operatorNote: plan.operatorNote || "",
      status: plan.status || "planned",
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.title || !form.startDate || !form.endDate) {
      addToast("Lütfen zorunlu alanları doldurun.", "error");
      return;
    }
    setFormLoading(true);
    try {
      const payload = {
        title: form.title,
        partId: form.partId || null,
        productionLineId: form.productionLineId || null,
        plannedQuantity: form.plannedQuantity
          ? Number(form.plannedQuantity)
          : 0,
        startDate: form.startDate,
        endDate: form.endDate,
        operatorNote: form.operatorNote,
        status: form.status,
      };
      if (editingPlan) {
        await axios.put(
          `${API_URL}/api/production-planning/plans/${editingPlan._id}`,
          payload,
          { headers: authHeaders() },
        );
        addToast("Plan güncellendi.");
      } else {
        await axios.post(`${API_URL}/api/production-planning/plans`, payload, {
          headers: authHeaders(),
        });
        addToast("Plan oluşturuldu.");
      }
      setModalOpen(false);
      fetchPlans();
    } catch (err) {
      addToast(err?.response?.data?.message || "Bir hata oluştu.", "error");
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/production-planning/plans/${id}`, {
        headers: authHeaders(),
      });
      addToast("Plan silindi.");
      setDeleteConfirm(null);
      fetchPlans();
    } catch {
      addToast("Silme başarısız.", "error");
    }
  };

  const handleStatusChange = async (plan, newStatus) => {
    try {
      await axios.patch(
        `${API_URL}/api/production-planning/plans/${plan._id}/status`,
        { status: newStatus },
        { headers: authHeaders() },
      );
      addToast("Durum güncellendi.");
      fetchPlans();
    } catch {
      addToast("Durum güncellenemedi.", "error");
    }
  };

  const resetShortageForm = () => {
    setShortageMainPartId("");
    setShortageVariantId("");
    setShortageModelId("");
    setShortageVariants([]);
    setShortageResult(null);
  };

  const runShortage = async () => {
    const isProductMode = shortageMode === "product";
    const effectivePartId = shortageVariantId || shortageMainPartId;

    if (isProductMode ? !shortageModelId : !effectivePartId) {
      addToast(
        isProductMode ? "Model ve miktar seçin." : "Parça ve miktar seçin.",
        "error",
      );
      return;
    }
    if (!shortageQty) {
      addToast("Miktar girin.", "error");
      return;
    }

    setShortageLoading(true);
    setShortageResult(null);
    try {
      const payload = isProductMode
        ? { deviceModelId: shortageModelId, quantity: Number(shortageQty) }
        : { partId: effectivePartId, quantity: Number(shortageQty) };
      const res = await axios.post(
        `${API_URL}/api/production-planning/shortage`,
        payload,
        { headers: authHeaders() },
      );
      setShortageResult(res.data?.data || null);
    } catch (err) {
      addToast(err?.response?.data?.message || "Hesaplama hatası.", "error");
    } finally {
      setShortageLoading(false);
    }
  };

  const planStats = {
    total: plans.length,
    planned: plans.filter((p) => p.status === "planned").length,
    inProgress: plans.filter((p) => p.status === "in_progress").length,
    completed: plans.filter((p) => p.status === "completed").length,
  };

  const allParts = Array.isArray(parts) ? parts : [];
  const partOptions = allParts.map((p) => ({
    value: p._id,
    label: `${p.name} (${p.code})`,
  }));
  const shortagePartOptions = allParts
    .filter((p) => !p.isFinalProduct)
    .map((p) => ({
      value: p._id,
      label: `${p.name} (${p.code})`,
    }));
  const modelOptions = (Array.isArray(deviceModels) ? deviceModels : []).map(
    (m) => ({
      value: m._id,
      label: `${m.code} — ${m.name}`,
    }),
  );
  const lineOptions = (Array.isArray(lines) ? lines : []).map((l) => ({
    value: l._id,
    label: l.name || l.code,
  }));

  // Group feasibility: main parts as rows, variants as sub-rows.
  const feasMainItems = feasibility.filter((f) => !f.mainPartId);
  const feasVariantMap = feasibility.reduce((acc, f) => {
    if (f.mainPartId) {
      if (!acc[f.mainPartId]) acc[f.mainPartId] = [];
      acc[f.mainPartId].push(f);
    }
    return acc;
  }, {});

  const groupedFeasibility = feasMainItems.map((main) => ({
    ...main,
    variants: feasVariantMap[main.partId] || [],
  }));

  // Also include parts that are variants but have no matching main (orphan variants shown standalone)
  const orphanVariants = feasibility.filter(
    (f) =>
      f.mainPartId &&
      !feasMainItems.find((m) => m.partId === f.mainPartId)
  );

  // Shortage tab: variant options for selected main part
  const shortageVariantOptions = shortageVariants.map((v) => ({
    value: v._id,
    label: `${v.name} (${v.code})`,
  }));

  return (
    <AppPage
      breadcrumbs={[{ name: "Üretim Yönetimi" }, { name: "Üretilebilirlik Analizi" }]}
    >
      <ToastContainer>
        {toasts.map((t) => (
          <Toast key={t.id} type={t.type}>
            {t.message}
          </Toast>
        ))}
      </ToastContainer>

      <div className="mlm-header card card-body p-3 mb-3">
        <div className="mlm-header-left">
          <h2 className="mlm-title">Üretilebilirlik Analizi</h2>
        </div>
      </div>
          <Panel>

            <Container>
              <ContainerItem>
                <Table
                  columns={FEASIBILITY_COLUMNS}
                  loading={feasibilityLoading}
                  search={true}
                >
                  {[...groupedFeasibility, ...orphanVariants].map((item) => {
                    const isOrphan = !!item.mainPartId;
                    const hasVariants = !isOrphan && item.variants?.length > 0;
                    const isExpanded = expandedFeasRow === item.partId;
                    const hasBom = item.bomLines?.length > 0;

                    return (
                      <React.Fragment key={item.partId}>
                        {/* ── Main part row ── */}
                        <Tr
                          style={{
                            cursor:
                              hasVariants || hasBom ? "pointer" : "default",
                          }}
                          onClick={() =>
                            (hasVariants || hasBom) &&
                            setExpandedFeasRow(isExpanded ? null : item.partId)
                          }
                        >
                          <Td>
                            <div className="d-flex align-items-center gap-2">
                              {(hasVariants || hasBom) && (
                                <span
                                  style={{
                                    color: "var(--m-text-subtle, #9ca3af)",
                                    fontSize: 12,
                                  }}
                                >
                                  {isExpanded ? (
                                    <FiChevronUp />
                                  ) : (
                                    <FiChevronDown />
                                  )}
                                </span>
                              )}
                              <div>
                                <div style={{ fontWeight: 600 }}>
                                  {item.partName}
                                </div>
                                <div
                                  style={{
                                    fontSize: 11,
                                    color: "var(--m-text-subtle, #9ca3af)",
                                  }}
                                >
                                  {item.partCode}
                                </div>
                              </div>
                              {item.isFinalProduct && (
                                <span
                                  className={styles.chip}
                                  style={{
                                    background: "#ede9fe",
                                    color: "#7c3aed",
                                  }}
                                >
                                  Nihai Ürün
                                </span>
                              )}
                              {hasVariants && (
                                <span
                                  className={styles.chip}
                                  style={{
                                    background: "#fef3c7",
                                    color: "#92400e",
                                  }}
                                >
                                  {item.variants.length} varyant
                                </span>
                              )}
                            </div>
                          </Td>
                          <Td>
                            <span
                              className={styles.chip}
                              style={
                                item.source === "purchased"
                                  ? { background: "#f3f4f6", color: "#374151" }
                                  : { background: "#dbeafe", color: "#1d4ed8" }
                              }
                            >
                              {item.source === "purchased"
                                ? "Satın Alınan"
                                : item.source === "produced"
                                  ? "Üretilen"
                                  : "Karma"}
                            </span>
                          </Td>

                          <Td>
                            <strong
                              style={{
                                fontSize: 15,
                                color:
                                  item.maxProducible > 0
                                    ? "#10b981"
                                    : item.bomSource === "none"
                                      ? "var(--m-text-subtle, #9ca3af)"
                                      : "#ef4444",
                              }}
                            >
                              {item.bomSource === "none"
                                ? "—"
                                : item.maxProducible}
                            </strong>
                          </Td>
                          <Td>
                            {item.missingComponents > 0 ? (
                              <span
                                style={{
                                  color: "#ef4444",
                                  fontWeight: 600,
                                  fontSize: 13,
                                }}
                              >
                                <FiAlertCircle style={{ marginRight: 4 }} />
                                {item.missingComponents} eksik
                              </span>
                            ) : item.bomSource !== "none" ? (
                              <span style={{ color: "#10b981", fontSize: 13 }}>
                                <FiCheckSquare style={{ marginRight: 4 }} />
                                Yeterli
                              </span>
                            ) : (
                              "—"
                            )}
                          </Td>

                        </Tr>

                        {/* ── Expanded BOM detail (no variants) ── */}
                        {isExpanded && !hasVariants && hasBom && (
                          <Tr>
                            <Td
                              colSpan={6}
                              style={{
                                padding: 0,
                                background: "var(--m-background, #f9fafb)",
                              }}
                            >
                              <BomDetailTable
                                bomLines={item.bomLines}
                                styles={styles}
                              />
                            </Td>
                          </Tr>
                        )}

                        {/* ── Expanded variant sub-rows ── */}
                        {isExpanded &&
                          hasVariants &&
                          item.variants.map((v) => (
                            <React.Fragment key={v.partId}>
                              <Tr
                                style={{
                                  background: "var(--m-background, #f8fafc)",
                                  cursor: v.bomLines?.length
                                    ? "pointer"
                                    : "default",
                                }}
                                onClick={() => {
                                  if (v.bomLines?.length) {
                                    setExpandedVariantRow(
                                      expandedVariantRow === v.partId ? null : v.partId
                                    );
                                  }
                                }}
                              >
                                <Td>
                                  <div
                                    className="d-flex align-items-center gap-2"
                                    style={{ paddingLeft: 28 }}
                                  >
                                    {v.bomLines?.length > 0 && (
                                      <span
                                        style={{
                                          color:
                                            "var(--m-text-subtle, #9ca3af)",
                                          fontSize: 12,
                                        }}
                                      >
                                        {expandedVariantRow === v.partId ? (
                                          <FiChevronUp />
                                        ) : (
                                          <FiChevronDown />
                                        )}
                                      </span>
                                    )}
                                    <div>
                                      <div style={{ fontWeight: 500 }}>
                                        {v.partName}
                                      </div>
                                      <div
                                        style={{
                                          fontSize: 11,
                                          color:
                                            "var(--m-text-subtle, #9ca3af)",
                                        }}
                                      >
                                        {v.partCode}
                                      </div>
                                    </div>
                                    <span
                                      className={styles.chip}
                                      style={{
                                        background: "#fef3c7",
                                        color: "#92400e",
                                      }}
                                    >
                                      Varyant
                                    </span>
                                  </div>
                                </Td>
                                <Td>
                                  <span
                                    className={styles.chip}
                                    style={
                                      v.source === "purchased"
                                        ? {
                                            background: "#f3f4f6",
                                            color: "#374151",
                                          }
                                        : {
                                            background: "#dbeafe",
                                            color: "#1d4ed8",
                                          }
                                    }
                                  >
                                    {v.source === "purchased"
                                      ? "Satın Alınan"
                                      : v.source === "produced"
                                        ? "Üretilen"
                                        : "Karma"}
                                  </span>
                                </Td>

                                <Td>
                                  <strong
                                    style={{
                                      fontSize: 15,
                                      color:
                                        v.maxProducible > 0
                                          ? "#10b981"
                                          : v.bomSource === "none"
                                            ? "var(--m-text-subtle, #9ca3af)"
                                            : "#ef4444",
                                    }}
                                  >
                                    {v.bomSource === "none"
                                      ? "—"
                                      : v.maxProducible}
                                  </strong>
                                </Td>
                                <Td>
                                  {v.missingComponents > 0 ? (
                                    <span
                                      style={{
                                        color: "#ef4444",
                                        fontWeight: 600,
                                        fontSize: 13,
                                      }}
                                    >
                                      <FiAlertCircle
                                        style={{ marginRight: 4 }}
                                      />
                                      {v.missingComponents} eksik
                                    </span>
                                  ) : v.bomSource !== "none" ? (
                                    <span
                                      style={{ color: "#10b981", fontSize: 13 }}
                                    >
                                      <FiCheckSquare
                                        style={{ marginRight: 4 }}
                                      />
                                      Yeterli
                                    </span>
                                  ) : (
                                    "—"
                                  )}
                                </Td>

                              </Tr>
                              {expandedVariantRow === v.partId &&
                                v.bomLines?.length > 0 && (
                                  <Tr>
                                    <Td
                                      colSpan={6}
                                      style={{
                                        padding: 0,
                                        background:
                                          "var(--m-background, #f9fafb)",
                                      }}
                                    >
                                      <BomDetailTable
                                        bomLines={v.bomLines}
                                        styles={styles}
                                        indent={28}
                                      />
                                    </Td>
                                  </Tr>
                                )}
                            </React.Fragment>
                          ))}
                      </React.Fragment>
                    );
                  })}
                </Table>
              </ContainerItem>
            </Container>
          </Panel>
    </AppPage>
  );
};

export default withAuth(Feasibility, ["admin", "employee"]);
